import { ReactElement, useEffect, useState } from 'react';
import { Card } from '../../../../components';
import { CircularProgress, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Config from 'Config';
import { AuthErrorResponseCode } from '../../../../common/types/auth-error-response';
import { HttpSessionBreakingError } from '../HttpSessionBreakingError/HttpSessionBreakingError';
import { SocketBreakingError } from '../SocketBreakingError/SocketBreakingError';

export const CallbackEpsHandled = () => {
  const { t } = useTranslation();

  const [responseReceived, setResponseReceived] = useState(false);
  const [error, setError] = useState<ReactElement | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const url = new URL(window.location.href);
        const state = url.searchParams.get('state') ?? '';
        const code = url.searchParams.get('code');
        const error = url.searchParams.get('error');
        const errorDescription = url.searchParams.get('error_description');

        const sessionRes = await fetch(Config.SESSION_DATA_URL, {
          method: 'GET',
          credentials: 'include',
        });

        if (!sessionRes.ok) {
          setError(<SocketBreakingError errorCode={AuthErrorResponseCode.INVALID_REQUEST} />);
          return;
        }

        const session = await sessionRes.json();
        const params: string | undefined = session.authorizeRequestParams;

        if (!params) {
          console.error('authorizeRequestParams missing in session response');
          setError(<SocketBreakingError errorCode={AuthErrorResponseCode.INVALID_REQUEST} />);
          return;
        }

        const callbackRes = await fetch(Config.EPS_CALLBACK_URL, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state, error, error_description: errorDescription }),
        });

        if (!callbackRes.ok) {
          if (callbackRes.status === 401) {
            setError(<HttpSessionBreakingError />);
          } else {
            const callbackError = await getEpsCallbackErrorCode(callbackRes);
            setError(<SocketBreakingError errorCode={callbackError} />);
          }
          return;
        }

        setResponseReceived(true);

        setTimeout(() => {
          const separator = Config.AUTHORIZE_URL.includes('?') ? '&' : '?';
          window.location.href = `${Config.AUTHORIZE_URL}${separator}${params}`;
        }, 2000);
      } catch (e) {
        console.error('error_handling_eps_callback', e);
        setError(<SocketBreakingError />);
      }
    };

    run();
  }, []);

  async function getEpsCallbackErrorCode(response: Response): Promise<AuthErrorResponseCode> {
    try {
      const body = await response.json();
      if (body?.error === 'callback_error') {
        return AuthErrorResponseCode.ACCESS_DENIED;
      }
      if (body?.error === 'invalid_request') {
        return AuthErrorResponseCode.INVALID_REQUEST;
      }
    } catch {
      return AuthErrorResponseCode.SERVER_ERROR;
    }
    return AuthErrorResponseCode.SERVER_ERROR;
  }

  return (
    error || (
      <Card>
        <div aria-live="polite">
          <Stack
            direction="column"
            sx={{
              flex: '1',
              gap: '2rem',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem 0',
            }}
          >
            <CircularProgress color="primary" />
            {responseReceived ? t('form.eps.Callback.Finished') : t('form.eps.Callback.Processing')}
          </Stack>
        </div>
      </Card>
    )
  );
};
